import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Cookie, Info, Clock, Settings, Shield, Globe } from 'lucide-react';
import SEO from '../components/SEO';

export const Cookies = () => {
  const { t } = useTranslation();

  const browsers = t('cookies.howToDisable.browsers.items', { returnObjects: true, defaultValue: [] });

  const Card: React.FC<{ icon: ReactNode; title: string; children: ReactNode }> = ({ icon, title, children }) => (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-3xl border border-white/10 bg-white/[0.02] px-6 py-8 md:px-10 md:py-10 hover:bg-white/[0.04] transition-colors duration-500"
    >
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 grid place-items-center text-cyan-400">
          {icon}
        </div>
        <h2 className="text-xl font-bold text-white leading-tight">{title}</h2>
      </div>
      <div className="space-y-4 text-base text-slate-400 font-light leading-relaxed">{children}</div>
    </motion.section>
  );

  return (
    <div className="relative min-h-screen pt-24 pb-16 px-4 bg-[#03050b] text-slate-200 selection:bg-purple-500/30">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-purple-500/5 blur-[120px] rounded-full" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-cyan-500/5 blur-[120px] rounded-full" />
      </div>
      
      <div className="relative max-w-5xl mx-auto z-10">
        <SEO
          title={`${t('navigation.legal.cookies') || 'Cookies'} – Victor Lazari`}
          description={t('cookies.title') as string}
          canonical={"https://victorlazari.com/cookies"}
          image={"https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"}
          type="website"
        />

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/5 text-purple-400">
            <Cookie className="w-4 h-4" />
            <span className="text-xs font-mono uppercase tracking-[0.3em]">Tracking Policy</span>
          </div>
          <h1 className="mt-8 text-4xl md:text-6xl font-black text-white tracking-tight leading-none">
            {t('cookies.title')}
          </h1>
        </motion.div>

        <div className="space-y-8">
          <Card icon={<Info className="w-5 h-5" />} title={t('cookies.intro.title')}>
            <p>{t('cookies.intro.content')}</p>
          </Card>

          <Card icon={<Cookie className="w-5 h-5" />} title={t('cookies.whatAreCookies.title')}>
            <p>{t('cookies.whatAreCookies.content')}</p>
          </Card>

          <Card icon={<Clock className="w-5 h-5" />} title={t('cookies.types.title')}>
            <p>{t('cookies.types.content')}</p>
            <div className="space-y-4 pt-4 border-t border-white/5">
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2">{t('cookies.types.session.title')}</h3>
                <p className="text-slate-400">{t('cookies.types.session.content')}</p>
              </div>
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2">{t('cookies.types.firstParty.title')}</h3>
                <p className="text-slate-400">{t('cookies.types.firstParty.content')}</p>
              </div>
            </div>
          </Card>

          <Card icon={<Shield className="w-5 h-5" />} title={t('cookies.cookiesWeUse.title')}>
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  {t('cookies.cookiesWeUse.necessary.title')}
                </h3>
                <p className="text-slate-400">{t('cookies.cookiesWeUse.necessary.content')}</p>
              </div>
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                  {t('cookies.cookiesWeUse.navigation.title')}
                </h3>
                <p className="text-slate-400">{t('cookies.cookiesWeUse.navigation.content')}</p>
              </div>
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                  {t('cookies.cookiesWeUse.thirdParty.title')}
                </h3>
                <p className="text-slate-400">{t('cookies.cookiesWeUse.thirdParty.content')}</p>
              </div>
            </div>
          </Card>

          <Card icon={<Globe className="w-5 h-5" />} title={t('cookies.consent.title')}>
            <p>{t('cookies.consent.content')}</p>
            <div className="mt-4 inline-block px-3 py-1 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs font-mono uppercase tracking-widest">
              {t('cookies.consent.status')}
            </div>
          </Card>

          <Card icon={<Settings className="w-5 h-5" />} title={t('cookies.howToDisable.title')}>
            <p>{t('cookies.howToDisable.content')}</p>
            <div className="space-y-6 pt-4 border-t border-white/5">
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3">{t('cookies.howToDisable.browsers.title')}</h3>
                {Array.isArray(browsers) && browsers.length > 0 && (
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {browsers.map((browser: any, index: number) =>
                      typeof browser === 'string' ? (
                        <li key={index} className="flex items-center gap-2 text-slate-400 bg-white/5 px-3 py-2 rounded-lg border border-white/5">
                          <div className="w-1 h-1 rounded-full bg-slate-500" />
                          {browser}
                        </li>
                      ) : null
                    )}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2">{t('cookies.howToDisable.analytics.title')}</h3>
                <p className="text-slate-400">{t('cookies.howToDisable.analytics.content')}</p>
              </div>
            </div>
          </Card>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xs text-slate-600 text-center mt-16 font-mono uppercase tracking-widest"
          >
            {t('cookies.lastUpdated')} // GDPR_COMPLIANCE_V2
          </motion.p>
        </div>
      </div>
    </div>
  );
};